import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";

/**
 * FullPageError - Full page error fallback
 * Use for app-level errors
 */
export const FullPageError = ({ error, resetError }) => {
  const { t } = useTran();
  const navigate = useNavigate();

  const handleGoHome = () => {
    resetError?.();
    navigate(PATHS.DASHBOARD);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <CardTitle className="text-2xl">
                {t("Something went wrong")}
              </CardTitle>
              <CardDescription>
                {t("An unexpected error occurred")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertTitle>{t("Error Details")}</AlertTitle>
            <AlertDescription className="mt-2 font-mono text-xs">
              {error?.message || t("Unknown error")}
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button onClick={resetError} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t("Try Again")}
            </Button>
            <Button variant="outline" onClick={handleGoHome} className="flex-1">
              <Home className="mr-2 h-4 w-4" />
              {t("Go Home")}
            </Button>
          </div>

          {process.env.NODE_ENV === "development" && error?.stack && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                {t("Show stack trace")}
              </summary>
              <pre className="mt-2 overflow-auto rounded-radius bg-muted p-4 text-xs">
                {error.stack}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * InlineError - Inline error fallback
 * Use for component-level errors
 */
export const InlineError = ({ error, resetError }) => {
  const { t } = useTran();

  return (
    <Alert variant="destructive" className="my-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t("error.label")}</AlertTitle>
      <AlertDescription className="mt-2">
        <p>{error?.message || t("Something went wrong")}</p>
        {resetError && (
          <Button
            variant="outline"
            size="sm"
            onClick={resetError}
            className="mt-2"
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            {t("Try Again")}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};

/**
 * RouteError - Route-level error fallback
 * Use for page/route-level errors
 */
export const RouteError = ({ error, resetError }) => {
  const { t } = useTran();
  const navigate = useNavigate();

  const handleGoBack = () => {
    resetError?.();
    navigate(-1);
  };

  const handleGoHome = () => {
    resetError?.();
    navigate(PATHS.DASHBOARD);
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4 text-center">
        <AlertCircle className="mx-auto h-16 w-16 text-destructive" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {t("Page Error")}
          </h2>
          <p className="mt-2 text-muted-foreground">
            {error?.message || t("This page encountered an error")}
          </p>
        </div>

        <div className="flex justify-center gap-2">
          <Button onClick={handleGoBack} variant="outline">
            {t("Go Back")}
          </Button>
          <Button onClick={handleGoHome}>
            <Home className="mr-2 h-4 w-4" />
            {t("Go Home")}
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && error?.stack && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              {t("Show stack trace")}
            </summary>
            <pre className="mt-2 overflow-auto rounded-radius bg-muted p-4 text-xs">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};
